import type { MapControl } from "../types";

export interface ToggleControlOptions {
    /** Accessible title / tooltip text. */
    title: string;
    /** Inline SVG (or HTML) for the button glyph. */
    iconHtml: string;
    /** Initial pressed state. */
    initial?: boolean;
    /** Fired with the new state whenever the user toggles. */
    onToggle: (active: boolean) => void;
}

/**
 * A square on-map toggle button that sits in a MapLibre control group, sized
 * and styled to match the native nav/fullscreen buttons. Used for X-Ray and
 * Tile Boundaries. Renderer-agnostic: implements {@link MapControl}; the engine
 * adapts it to MapLibre's IControl, so this never imports `maplibre-gl`.
 */
export class ToggleControl implements MapControl {
    private container: HTMLDivElement | null = null;
    private button: HTMLButtonElement | null = null;
    private active: boolean;
    private readonly options: ToggleControlOptions;

    constructor(options: ToggleControlOptions) {
        this.options = options;
        this.active = options.initial ?? false;
    }

    onAdd(): HTMLElement {
        const container = document.createElement("div");
        // Reuse MapLibre's control-group styling so it matches native buttons.
        container.className = "maplibregl-ctrl maplibregl-ctrl-group";

        const button = document.createElement("button");
        button.type = "button";
        button.title = this.options.title;
        button.setAttribute("aria-label", this.options.title);
        button.setAttribute("aria-pressed", String(this.active));
        button.innerHTML = this.options.iconHtml;
        this.applyActiveStyle(button);
        button.addEventListener("click", this.handleClick);

        container.appendChild(button);
        this.container = container;
        this.button = button;
        return container;
    }

    onRemove(): void {
        this.button?.removeEventListener("click", this.handleClick);
        this.container?.parentNode?.removeChild(this.container);
        this.container = null;
        this.button = null;
    }

    /** Programmatically set the toggle state (e.g. from store sync). */
    setActive(active: boolean): void {
        this.active = active;
        if (this.button) {
            this.button.setAttribute("aria-pressed", String(active));
            this.applyActiveStyle(this.button);
        }
    }

    private handleClick = (): void => {
        this.active = !this.active;
        if (this.button) {
            this.button.setAttribute("aria-pressed", String(this.active));
            this.applyActiveStyle(this.button);
        }
        this.options.onToggle(this.active);
    };

    /** Tints the button when active so the on-state is visible. */
    private applyActiveStyle(button: HTMLButtonElement): void {
        button.style.backgroundColor = this.active ? "#ec7211" : "";
        button.style.color = this.active ? "#fff" : "";
    }
}
